<template>
  <WidgetScrollCard
    header="Project Aging"
    icon="stopwatch"
    :subtitle="agingSubtitle"
    :height
    button-icon="cog"
    @header-clicked="showProjectAgingRange = true"
  >
    <template #content>
      <div v-if="agingProjects.length !== 0" class="flex flex-col gap-3">
        <div class="grid grid-cols-2 gap-3">
          <div v-for="(project, index) in agingProjects">
            <Card class="h-full" pt:body="p-2">
              <template #content>
                <div class="flex flex-row gap-5">
                  <div class="flex flex-col justify-center align-middle ml-2 text-primary-500 dark:text-primary-400">
                    <p class="text-sm font-bold mx-auto">Draw</p>
                    <p class="text-sm font-bold text-center">{{ project.currentDrawNumber }}</p>
                  </div>
                  <div class="flex flex-col text-sm">
                    <p class="w-fit text-base text-secondary-500 dark:text-secondary-400 font-bold">
                      {{ project.projectName }} ({{ project.projectCode }})
                    </p>

                    <div v-if="project.currentDrawDateCreated" class="flex flex-row text-surface-500 dark:text-surface-400 gap-5">
                      <p class="w-44">
                        Draw Created:
                        <span class="font-bold">{{
                          project.currentDrawDateCreated != null ? format(project.currentDrawDateCreated, "MM/dd/yy") : ""
                        }}</span>
                      </p>
                      <p>
                        Days Since: <span class="font-bold">{{ project.daysSinceDrawCreated }}</span>
                      </p>
                    </div>
                    <div v-if="project.lastApprovedDate" class="flex flex-row text-surface-500 dark:text-surface-400 gap-5">
                      <p class="w-44">
                        Last Approved:
                        <span class="font-bold">{{ project.lastApprovedDate != null ? format(project.lastApprovedDate, "MM/dd/yy") : "" }}</span>
                      </p>
                      <p>
                        Days Since: <span class="font-bold">{{ project.daysSinceLastApproved }}</span>
                      </p>
                    </div>
                    <div v-if="project.lastInspectedDate" class="flex flex-row text-surface-500 dark:text-surface-400 gap-5">
                      <p class="w-44">
                        Last Inspection:
                        <span class="font-bold">{{ project.lastInspectedDate != null ? format(project.lastInspectedDate, "MM/dd/yy") : "" }}</span>
                      </p>
                      <p>
                        Days Since: <span class="font-bold">{{ project.daysSinceLastInspected }}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </div>
      <div v-else class="h-full flex">
        <p class="text-center font-bold w-full mt-28">---No Aging Projects to Show---</p>
      </div>
    </template>
  </WidgetScrollCard>

  <TSDialog label="Change Project Aging Range" v-model:visible="showProjectAgingRange" icon="stopwatch" size="w-[600px]!">
    <template #content>
      <ProjectAgingRange
        v-model:project-aging-range="projectAgingRange"
        @cancelled="showProjectAgingRange = false"
        @submitted="refreshAgingProjects"
      ></ProjectAgingRange>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import type { ProjectAgingRange } from "@/types/Dashboard";
import { AgingProjectSchema, type AgingProject, type ProjectSummary } from "@/types/Projects";
import { format } from "date-fns";
import z from "zod";

const agingProjects = defineModel<AgingProject[]>("agingProjects", { required: true });
const projectAgingRange = defineModel<ProjectAgingRange>("projectAgingRange", { required: true });

interface Props {
  height: string;
}
const props = defineProps<Props>();

const showProjectAgingRange = ref(false);

const agingSubtitle = computed(() => {
  let subtitle = "Aging For: ";
  if (projectAgingRange.value.days > 0) subtitle += projectAgingRange.value.days.toString() + " Day(s)";
  if (projectAgingRange.value.months > 0) subtitle += projectAgingRange.value.months.toString() + " Month(s)";
  if (projectAgingRange.value.years > 0) subtitle += projectAgingRange.value.years.toString() + " Year(s)";
  return subtitle;
});

const refreshAgingProjects = async (): Promise<void> => {
  await axios.post("/projects/project-aging-range/update", projectAgingRange.value).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        agingProjects.value = z.array(AgingProjectSchema).parse(data);
        success("Aging Date Range Updated!", "");
      },
      (errors: ApiError[]) => {
        if (errors.length > 0) error("Error refreshing aging projects", errors[0].message);
      }
    );
  });

  showProjectAgingRange.value = false;
};
</script>
